import React, { useState } from "react";
import FilterBar, { computeRange, useSharedFilter } from "../FilterBar";
import { buildParams } from "../api";
import { useAdminData } from "../useAdminData";
import { Panel, KpiCard, MetricStrip, Skeleton, EmptyState, ErrorState, PageHeader, fmtNum } from "../Primitives";
import { LineChart, BarList, Donut, Funnel, HourBars } from "../Charts";
import { GAME_BY_ID, platformLabel, PROVIDER_NAMES } from "../catalog";

const defaultFilter = () => ({ preset: "30d", ...computeRange("30d"), platform: "", provider: "", game: "", device: "", campaign: "", compare: false });

function GameLabel({ it }) {
  const g = GAME_BY_ID[it.key];
  return (
    <span className="flex items-center gap-2 min-w-0">
      {g?.image && <img src={g.image} alt="" className="w-5 h-5 rounded-[3px] object-cover shrink-0" loading="lazy" />}
      <span className="truncate">{it.name || g?.name || it.key}</span>
    </span>
  );
}

export default function Dashboard() {
  const [filter, setFilter] = useSharedFilter(defaultFilter);
  const { data, loading, error, reload } = useAdminData(async (api) => {
    const params = buildParams(filter);
    const [ov, ts, hr, fn] = await Promise.all([
      api.get("/admin/overview", { params: { ...params, compare: filter.compare || undefined } }),
      api.get("/admin/timeseries", { params: { ...params, metric: "clicks" } }),
      api.get("/admin/by-hour", { params }),
      api.get("/admin/funnel", { params }),
    ]);
    return { ...ov.data, series: ts.data.series, hours: hr.data.hours, funnel: fn.data };
  }, [filter]);

  const k = data?.kpis;
  const p = data?.previous?.kpis;
  const conv = data?.conversions;
  const [unit, setUnit] = useState("sessions");
  const cv = conv?.[unit];
  const pcv = data?.previous?.conversions?.[unit];
  const unitLabel = unit === "visitors" ? "visitantes" : "sessões";
  const isEmpty = !loading && k && k.total_events === 0;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Visão geral" meta="Indicadores calculados a partir dos eventos registrados no site público." />
      <FilterBar value={filter} onChange={setFilter} show={{ game: true, campaign: true, compare: true }} />
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.previous_unavailable && filter.compare && <p className="ops-help mb-3" data-testid="compare-unavailable">{data.previous_unavailable}</p>}
      {isEmpty && (
        <p className="text-[13px] text-[var(--muted)] mb-4 pl-3 border-l-2 border-[var(--border-strong)]" data-testid="dashboard-empty">
          Nenhum evento registrado neste período. Os indicadores ficam em zero até que visitantes interajam com o site.
        </p>
      )}

      {loading && !data ? <Skeleton className="h-[170px] mb-4" /> : k && (
        <>
          <MetricStrip cols={6} className="mb-3">
            <KpiCard label="Visitantes" value={k.visitors} prev={p?.visitors} hint="ID anônimo por navegador" />
            <KpiCard label="Sessões" value={k.sessions} prev={p?.sessions} hint="Expira após 30 min sem atividade" />
            <KpiCard label="Jogos abertos" value={k.game_opens} prev={p?.game_opens} />
            <KpiCard label="Sinais vistos" value={k.signal_views} prev={p?.signal_views} />
            <KpiCard label="Cliques de saída" value={k.exit_clicks} prev={p?.exit_clicks} hint="Botão para a plataforma" />
            <KpiCard label="Cliques totais" value={k.total_clicks} prev={p?.total_clicks} hint="Saída, WhatsApp, Telegram, cópia" />
          </MetricStrip>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-2">
            <h2 className="ops-label">Conversão e funil por</h2>
            <div className="ops-seg" role="group" aria-label="Unidade de conversão e funil">
              <button aria-pressed={unit === "sessions"} onClick={() => setUnit("sessions")} data-testid="funnel-unit-sessions">Sessões</button>
              <button aria-pressed={unit === "visitors"} onClick={() => setUnit("visitors")} data-testid="funnel-unit-visitors">Visitantes</button>
            </div>
          </div>
          <MetricStrip cols={4} className="mb-6">
            <KpiCard label="Escolheram provedor" value={cv?.provider_rate} prev={pcv?.provider_rate} suffix="%" decimals={1} hint={`${fmtNum(cv?.provider)} de ${fmtNum(cv?.base)} ${unitLabel}`} testid="conv-provider" />
            <KpiCard label="Abriram jogo" value={cv?.game_rate} prev={pcv?.game_rate} suffix="%" decimals={1} hint={`${fmtNum(cv?.game)} de ${fmtNum(cv?.base)} ${unitLabel}`} testid="conv-game" />
            <KpiCard label="Clicaram para a plataforma" value={cv?.exit_rate} prev={pcv?.exit_rate} suffix="%" decimals={1} hint={`${fmtNum(cv?.exit)} de ${fmtNum(cv?.base)} ${unitLabel}`} testid="conv-exit" />
            <KpiCard label="Cliques hoje · 7d · 30d" value={k.clicks_today} hint={`${fmtNum(k.clicks_7d)} em 7 dias · ${fmtNum(k.clicks_30d)} em 30 dias`} testid="kpi-click-windows" />
          </MetricStrip>
        </>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-4 mb-4">
        <Panel title={`Funil de navegação · ${unitLabel}`} testid="funnel-panel">
          {loading && !data ? <div className="p-4"><Skeleton className="h-40" /></div> : data && (
            <>
              <Funnel stages={unit === "sessions" ? data.funnel.funnel : data.funnel.funnel_visitors} unitLabel={unit === "sessions" ? "Sessões" : "Visitantes"} />
              <ul className="px-4 py-3 space-y-1 ops-help border-t border-[var(--border)]" data-testid="funnel-notes">
                <li>{data.funnel.notes.rate}</li>
                <li>{data.funnel.notes.step_rate}</li>
                <li>{data.funnel.notes.optional}</li>
              </ul>
            </>
          )}
        </Panel>
        <Panel title="Cliques por dia">
          {loading && !data ? <div className="p-4"><Skeleton className="h-[170px]" /></div>
            : data?.series?.length ? <LineChart data={data.series} /> : <EmptyState title="Sem cliques no período" />}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel title="Participação por plataforma">
          {data?.top_platforms?.length ? <Donut items={data.top_platforms.map((x) => ({ label: platformLabel(x.key), value: x.count }))} /> : <EmptyState />}
        </Panel>
        <Panel title="Provedores">
          {data?.top_providers?.length ? <BarList items={data.top_providers.map((x) => ({ name: PROVIDER_NAMES[x.key] || x.key, count: x.count }))} /> : <EmptyState />}
        </Panel>
        <Panel title="Jogos mais acessados">
          {data?.top_games?.length ? <BarList items={data.top_games} renderLabel={(it) => <GameLabel it={it} />} /> : <EmptyState title="Sem aberturas de jogos" />}
        </Panel>
      </div>

      <Panel title="Eventos por hora (horário de Brasília)">
        {data ? <HourBars hours={data.hours} /> : <div className="p-4"><Skeleton className="h-24" /></div>}
      </Panel>
    </div>
  );
}
